import {isPlatformBrowser, isPlatformServer} from '@angular/common';
import {Component, inject, OnDestroy, PLATFORM_ID} from '@angular/core';
import {MatSnackBar} from '@angular/material/snack-bar';
import {NavigationEnd, Router} from '@angular/router';
import {SwUpdate, VersionEvent} from '@angular/service-worker';
import {APP_CONFIG} from '@core-app/app.config';
import {NotificationService} from '@core-app/services/notification.service';
import {X_SERVICE_ZONE} from '@core-app/tokens';
import {
  AnalyticsConfiguration,
  AnalyticsService,
  ApiService,
  AppState,
  BrandColors,
  BrandingService,
  CardsService,
  CONFIG,
  pickContrastingTextColor,
  TranslateService,
  updateAppConfig,
  WindowService,
} from '@gallaghercp/core-frontend';
import {Store} from '@ngrx/store';
import {TranslateService as TranslateProvider} from '@ngx-translate/core';
import {filter, Subscription} from 'rxjs';

@Component({
  selector: 'core-root',
  template: '<router-outlet></router-outlet>',
  standalone: false,
})
export class AppComponent implements OnDestroy {
  title = 'core-app';
  subscription: Subscription = new Subscription();
  private translate = inject(TranslateService);
  private translateProvider = inject(TranslateProvider);
  private analytics = inject(AnalyticsService);
  private api = inject(ApiService);
  private windowService = inject(WindowService);
  private store = inject<Store<AppState>>(Store);
  private router = inject(Router);
  private updates = inject(SwUpdate);
  private snackBar = inject(MatSnackBar);
  private notification = inject(NotificationService);
  private uiBranding = inject(BrandingService);
  private zone = inject(X_SERVICE_ZONE);
  private platformId = inject(PLATFORM_ID);

  constructor() {
    this.analytics.initialise({
      gtm: {id: APP_CONFIG.gtm, consent: 3},
    } as AnalyticsConfiguration);

    if (this.zone && (isPlatformServer(this.platformId) || isPlatformBrowser(this.platformId))) {
      this.api.setServiceZone(Number(this.zone));
    }

    ((APP_CONFIG as unknown as CONFIG).domainsToZones ?? []).forEach((mapping: any) => {
      if (this.windowService.location?.hostname === mapping.domain) {
        this.api.setServiceZone(mapping.zone);
      }
    });

    if ((APP_CONFIG as unknown as CONFIG).benefitPIDS) {
      CardsService.PIDS_BENEFITS = (APP_CONFIG as unknown as CONFIG).benefitPIDS ?? [2];
    }

    if ((APP_CONFIG as unknown as CONFIG)['articlesParentRoute']) {
      CardsService.ARTICLE_PATH = (APP_CONFIG as unknown as CONFIG)['articlesParentRoute'];
    }

    const colors: BrandColors | null = this.uiBranding.getBrandColors();
    const configColors = (APP_CONFIG as any).colors;
    const navBg = colors?.bg ?? configColors?.elementBackgroundColour;

    if (navBg) {
      // Link colors should be determined by background contrast
      const contrastColor = pickContrastingTextColor(navBg);
      const navLink = contrastColor === 'white' ? '#ffffff' : '#000000';
      const navLinkActive = navLink;

      document.documentElement.style.setProperty('--color-nav-bg', navBg);
      document.documentElement.style.setProperty('--color-bkg-pre-login', navBg);
      document.documentElement.style.setProperty('--color-bkg-pre-login-bg-content', contrastColor);
      document.documentElement.style.setProperty('--color-nav-link', navLink);
      document.documentElement.style.setProperty('--color-nav-link-active', navLinkActive);

      // Set footer variables as well
      document.documentElement.style.setProperty('--color-footer-bg', navBg);
      document.documentElement.style.setProperty('--color-footer-text', navLink);
    }

    if ((APP_CONFIG as any).navigation?.['navbarBrandWidth']) {
      document.documentElement.style.setProperty(
        '--brand-logo-width',
        (APP_CONFIG as any).navigation?.['navbarBrandWidth'],
      );
    }

    if (APP_CONFIG.multilingual) {
      // The default language for Bedrocc is set as en_GB, but that may not be the default language for the specific instance.
      // 1. Set local copies of the default & current language variables
      let defaultLanguageForApplication = this.translate.getDefaultLanguage();
      const currentLanguageForApplication = this.translate.getCurrentLanguage() || defaultLanguageForApplication;

      // 2. Register the available languages
      this.subscription.add(
        this.translate.getLanguages().subscribe((languages: any[]) => {
          const languageCodes = languages.map((language: any) => language.code);
          this.translateProvider.addLangs(languageCodes);

          // 3. Validation: If the default isn't in the list, fallback to the first one
          if (languageCodes.length > 0 && !languageCodes.includes(defaultLanguageForApplication)) {
            defaultLanguageForApplication = languageCodes[0];
          }

          // 3.5. If the current language isn't in the list, we also need to assume the user hasn't chosen a language yet.
          // In this case, we also set the current language to the first available language
          if (languageCodes.length > 0 && !languageCodes.includes(currentLanguageForApplication)) {
            this.translate.setCurrentLanguage(languageCodes[0]);
            this.translateProvider.use(languageCodes[0]);
          }
        }),
      );

      // 3. Set the default language to the correct value, as defined by defaultLanguageForApplication
      this.translateProvider.setDefaultLang(defaultLanguageForApplication);
    }

    this.subscription.add(
      this.router.events
        .pipe(filter(event => event instanceof NavigationEnd))
        .subscribe((event: NavigationEnd) => {
          const url = event.urlAfterRedirects;
          if (!url.startsWith('/ret')) {
            this.store.dispatch(updateAppConfig({lastVisitedUrl: url}));
          }
          const navigation = this.router.getCurrentNavigation();
          const fromUrl = navigation?.extras?.state?.['from'];
          this.store.dispatch(updateAppConfig({fromUrl}));
        }),
    );

    if (isPlatformBrowser(this.platformId) && (APP_CONFIG as any).cookiePolicy) {
      const script = document.createElement('script');
      script.src = '//policy.cookiereports.com/' + (APP_CONFIG as any).cookiePolicy + '_panel-en-gb.js';
      document.body.appendChild(script);
    }

    if (isPlatformBrowser(this.platformId) && navigator && (APP_CONFIG as any).pwa?.enabled) {
      try {
        navigator.clearAppBadge();
      } catch (e) {
        console.warn(e);
      }

      if (this.updates.isEnabled) {
        this.updates.versionUpdates.subscribe((evt: VersionEvent) => {
          switch (evt.type) {
            case 'VERSION_DETECTED':
              console.log(`Downloading new app version: ${evt.version.hash}`);
              break;
            case 'VERSION_READY':
              console.log(`Current app version: ${evt.currentVersion?.hash}`);
              console.log(`New app version ready for use: ${evt.latestVersion.hash}`);
              try {
                navigator.setAppBadge(1);
              } catch (e) {
                console.warn(e);
              }
              this.promptUser();
              break;
            case 'VERSION_INSTALLATION_FAILED':
              console.log(`Failed to install app version '${evt.version.hash}': ${evt.error}`);
              break;
          }
        });
      }

      if ((APP_CONFIG as any).pwa?.notifications) {
        // Register Firebase messaging service worker (must exist separately at public root)
        navigator.serviceWorker?.register('/firebase-messaging-sw.js', {
          type: 'module',
        });

        // Modular Firebase Messaging handled in NotificationService
        this.notification.subscribeToNotifications();
        this.notification.requestPermission();
        this.notification.receiveMessage();
      }
    }

    if ((APP_CONFIG as any).pillars) {
      CardsService.PILLARS = (APP_CONFIG as any).pillars as string[];
    }

  }

  promptUser(): void {
    const snackBarRef = this.snackBar.open('A new version of Core Bedrocc is available', 'Refresh');
    snackBarRef.onAction().subscribe(() => {
      this.windowService.location?.reload();
    });
  }

  ngOnDestroy(): void {
    this.subscription.unsubscribe();
  }
}
